import React, { Component, ErrorInfo } from 'react';
import { Navigate } from 'react-router-dom';

interface Props {
  children: React.ReactNode;
  // Список типов ошибок, которые нужно обрабатывать редиректом на страницу ошибки
  redirectErrors?: string[];
  // Функция для кастомной обработки ошибок
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  shouldRedirect: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      shouldRedirect: false
    };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Отправляем ошибку в систему аналитики если нужно
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    const { redirectErrors, onError } = this.props;

    // Если передана функция кастомной обработки, вызываем её
    if (onError) {
      onError(error, errorInfo);
    }

    // Проверяем, нужно ли редиректить на страницу ошибки
    const shouldRedirect = redirectErrors?.some(errorType => 
      error.name === errorType || 
      error.message.includes(errorType)
    ) ?? false;

    this.setState({ shouldRedirect });
  }

  render() {
    const { hasError, error, shouldRedirect } = this.state;

    // Если есть ошибка и нужно редиректить
    if (hasError && shouldRedirect) {
      return (
        <Navigate 
          to="/error" 
          state={{ 
            message: error?.message || 'Произошла непредвиденная ошибка'
          }} 
        />
      );
    }

    // Если есть ошибка, но редирект не нужен - показываем fallback UI
    if (hasError) {
      return (
        <div className="p-4 text-center">
          <p className="text-red-500">
            Произошла ошибка при загрузке компонента
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 